import {LoaderCircle} from "lucide-react";
import {useRef, useState} from "react";
import type {DragEvent} from "react";
import {srcUrl} from "@/api/ImageApi.ts";
import FileInputField from "@/components/forms/FileInputField.tsx";
import {Image} from "@/images/Image.ts";
import {cn} from "@/lib/utils.ts";

export type ImageUploadFieldProps = {
    image?: Image;
    uploading: boolean;
    onFileSelected: (file: File) => void;
    alt: string;
    className?: string;
};

export default function ImageUploadField({
    image,
    uploading,
    onFileSelected,
    alt,
    className,
}: ImageUploadFieldProps) {
    const dragDepth = useRef(0);
    const [dragging, setDragging] = useState(false);
    const dropActive = dragging && !uploading;

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        dragDepth.current += 1;
        if (!uploading) setDragging(true);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = uploading ? "none" : "copy";
    };

    const handleDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (uploading) return;
        const file = Array.from(event.dataTransfer.files).find(file => file.type.startsWith("image/"));
        if (file) onFileSelected(file);
    };

    return (
        <div className={cn(
            "group relative flex min-h-0 items-center justify-center overflow-hidden bg-black/20 p-2",
            className,
        )} aria-busy={uploading}
             onDragEnter={handleDragEnter}
             onDragOver={handleDragOver}
             onDragLeave={handleDragLeave}
             onDrop={handleDrop}>
            {image && <img src={srcUrl(image)} alt={alt} className="size-full object-contain"/>}
            <FileInputField label={image ? "Bild ändern" : "Bild auswählen"}
                            labelClassName={image
                                ? "absolute inset-0 h-auto rounded-none border-0 bg-black/60 text-white opacity-0 transition-opacity duration-200 hover:bg-black/60 group-hover:opacity-100"
                                : "absolute inset-4 h-auto flex-col rounded-lg border-2 border-dashed bg-transparent text-muted-foreground transition-colors hover:bg-accent/20 hover:text-foreground"}
                            onFileSelected={onFileSelected}
                            accept="image/*"
                            disabled={uploading}/>
            {dropActive && (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed border-primary bg-background/90 text-sm font-medium"
                     role="status">
                    Bild hier ablegen
                </div>
            )}
            {uploading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-background/80 text-sm"
                     role="status">
                    <LoaderCircle className="animate-spin"/> Bild wird hochgeladen…
                </div>
            )}
        </div>
    );
}
